/**
 * Help articles: Markdown files with a small header, one per feature. Pure functions, so the
 * check that every screen has an article runs in plain Node as well as in the browser.
 */

export type Audience = 'everyone' | 'admin' | 'user' | 'staff';

export interface HelpArticle {
  id: string;
  title: string;
  section: string;
  audience: Audience;
  /** Screens this article explains: "/t/*\/people" (* is one path segment). */
  routes: string[];
  summary: string;
  order: number;
  body: string;
  /** Images the body shows, by file name under /help-images. */
  images: string[];
}

/** Sections in the order the help center lists them. */
export const SECTIONS = ['Getting started', 'Messages', 'People and phones', 'Call routing', 'Contact center', 'Your company', 'Your phone', 'Your account', 'Stitch Admin'];

const AUDIENCES: Audience[] = ['everyone', 'admin', 'user', 'staff'];

export function parseArticle(source: string, file = 'article'): HelpArticle {
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(source.replace(/\r\n/g, '\n'));
  if (!m) throw new Error(`${file}: missing the --- header`);
  const head: Record<string, string> = {};
  for (const line of m[1]!.split('\n')) {
    const i = line.indexOf(':');
    if (i > 0) head[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  const need = (k: string) => {
    if (!head[k]) throw new Error(`${file}: missing "${k}"`);
    return head[k]!;
  };
  const audience = (head.audience ?? 'everyone') as Audience;
  if (!AUDIENCES.includes(audience)) throw new Error(`${file}: audience must be one of ${AUDIENCES.join(', ')}`);
  const section = need('section');
  if (!SECTIONS.includes(section)) throw new Error(`${file}: section must be one of ${SECTIONS.join(', ')}`);
  const id = need('id');
  if (!/^[a-z0-9-]+$/.test(id)) throw new Error(`${file}: id must be lower-case words joined by dashes`);
  const body = m[2]!.trim();
  return {
    id,
    title: need('title'),
    section,
    audience,
    routes: (head.routes ?? '')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean),
    summary: need('summary'),
    order: Number(head.order ?? 100) || 100,
    body,
    images: [...body.matchAll(/!\[[^\]]*\]\(([^)\s]+)\)/g)].map((x) => x[1]!.replace(/^\/?help-images\//, ''))
  };
}

function routeRegex(pattern: string): RegExp {
  const parts = pattern
    .replace(/\/+$/, '')
    .split('/')
    .map((seg) => (seg === '*' ? '[^/]+' : seg === '**' ? '.*' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')));
  return new RegExp(`^${parts.join('/')}/?$`);
}

/** The article for a screen: the most specific pattern that matches. */
export function articleForPath(articles: readonly HelpArticle[], path: string): HelpArticle | undefined {
  let best: { a: HelpArticle; score: number } | undefined;
  for (const a of articles) {
    for (const r of a.routes) {
      if (!routeRegex(r).test(path)) continue;
      const score = r.split('/').filter((s) => s && s !== '*' && s !== '**').length * 10 + r.length;
      if (!best || score > best.score) best = { a, score };
    }
  }
  return best?.a;
}

/** Who sees an article in the help center. */
export function visibleTo(a: HelpArticle, role: 'staff' | 'admin' | 'user' | null): boolean {
  if (role === 'staff') return true;
  if (a.audience === 'staff') return false;
  if (role === 'user') return a.audience === 'user' || a.audience === 'everyone';
  return true;
}

/** Plain words of an article, for search. */
export function plainText(a: HelpArticle): string {
  return `${a.title} ${a.summary} ${a.body}`
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_`>|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Articles for a search, best first: title words count most. */
export function searchArticles(articles: readonly HelpArticle[], query: string): HelpArticle[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  if (!words.length) return [];
  return articles
    .map((a) => {
      const title = a.title.toLowerCase();
      const text = plainText(a);
      let score = 0;
      for (const w of words) {
        if (title.includes(w)) score += 10;
        if (a.summary.toLowerCase().includes(w)) score += 4;
        if (text.includes(w)) score += 1;
        else return { a, score: 0 };
      }
      return { a, score };
    })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score)
    .map((x) => x.a);
}
